import { fetchServer } from "@/features/utils/fetch";
import type {
  SendSensitiveEmailCodeResponse,
  Setup2faResponse,
  TryConnectionResponse,
  ValidationSensitiveEmailCodeResponse
} from "../types";
import { ApiError } from "@/utils/internal-code-handler";

export class SecurityApiClient {
  static async is2faActive() {
    const res = await fetchServer("/two_factory/check_activation");
    return (await res.json()).data.isTwoFaActivated as boolean;
  }

  static async setup2fa() {
    const res = await fetchServer("/two_factory/setup");
    return (await res.json()).data as Setup2faResponse;
  }

  static async activate2fa(otp: string, secret: string) {
    const res = await fetchServer("/two_factory/activate", {
      body: {
        otp,
        secret
      }
    });
    return res.status === 200;
  }

  static async updatePassword(oldPassword: string, password: string) {
    const response = await fetchServer("/user/settings/update_password", {
      body: {
        oldPassword,
        password
      }
    });
    const success = response.status === 200 ? true : false;
    const res = await response.json();
    if (!success) {
      return { success, errorCode: res.code };
    } else {
      return { success, jwt: res.jwt };
    }
  }

  static async tryConnectViaOTP(email: string, password: string, code = "") {
    const response = await fetchServer("/user/settings/pre_update_pwd/check_otp_code", {
      body: {
        email,
        password,
        code
      }
    });
    if (!response.ok) throw new ApiError(await response.json(), response.status);

    return await response.json();
  }

  static async reset2Fa() {
    const response = await fetchServer("/two_factory/reset");
    const res = await response.json();
    return res.data;
  }

  static async openDemandOfAuthentication() {
    const response = await fetchServer("/authentication/add_trace_auth");
    const res = await response.json();
    return res as TryConnectionResponse;
  }

  static async updateDemandOfAuthentication(
    id: number,
    success: boolean,
    web_authn_signing_action_id: string | null,
    user_otp_input: string
  ) {
    const response = await fetchServer("/authentication/update_trace_auth", {
      method: "POST",
      reloadOn401: false,
      body: {
        id,
        success,
        web_authn_signing_action_id,
        user_otp_input
      }
    });
    const res = await response.json();
    return res as TryConnectionResponse;
  }

  static async requestSensitiveEmail(
    email: string,
    language: string,
    actionType: string | undefined,
    actionData: string | undefined,
    id?: string
  ) {
    const response = await fetchServer(
      id
        ? `/authentication/request_email_code?contextLegalEntityUuid=${id}`
        : "/authentication/request_email_code",
      {
        method: "POST",
        reloadOn401: false,
        body: {
          email,
          language,
          actionType,
          actionData
        }
      }
    );
    const res = await response.json();
    return res as SendSensitiveEmailCodeResponse;
  }
  static async validateSensitiveEmail(
    uuidSensitiveActionValidation: string,
    code: string,
    actionType: string
  ): Promise<ValidationSensitiveEmailCodeResponse> {
    const response = await fetchServer("/authentication/validate_email_code", {
      method: "POST",
      reloadOn401: false,
      body: {
        uuidSensitiveActionValidation,
        code,
        actionType
      }
    });
    const res = await response.json();
    return res as ValidationSensitiveEmailCodeResponse;
  }
}
